import React from "react";

/**
 * Curated icon library for Animaux Tags.
 * - Designed in 24×24 viewBox.
 * - Filled silhouettes optimised for laser engraving on small metal tags.
 * - Each key matches a standalone SVG file under /grabado/ folder.
 */

export const ICON_CATEGORIES = [
  { key: "animaux", fr: "Animaux", en: "Animals", es: "Animales" },
  { key: "symboles", fr: "Symboles", en: "Symbols", es: "Símbolos" },
];

// "none" is always rendered first, separately
export const ICON_OPTIONS = [
  { key: "none",        category: null,       fr: "Aucun",        en: "None",       es: "Ninguno" },

  // --- Animaux ---
  { key: "heart",       category: "animaux",  fr: "Cœur",         en: "Heart",      es: "Corazón" },
  { key: "paw",         category: "animaux",  fr: "Patte",        en: "Paw",        es: "Pata" },
  { key: "bone",        category: "animaux",  fr: "Os",           en: "Bone",       es: "Hueso" },
  { key: "fish",        category: "animaux",  fr: "Poisson",      en: "Fish",       es: "Pez" },
  { key: "bird",        category: "animaux",  fr: "Oiseau",       en: "Bird",       es: "Ave" },
  { key: "rabbit",      category: "animaux",  fr: "Lapin",        en: "Rabbit",     es: "Conejo" },
  { key: "mouse",       category: "animaux",  fr: "Souris",       en: "Mouse",      es: "Ratón" },
  { key: "bowl",        category: "animaux",  fr: "Croquettes",   en: "Food bowl",  es: "Comida" },

  // --- Symboles ---
  { key: "star",        category: "symboles", fr: "Étoile",       en: "Star",       es: "Estrella" },
  { key: "moon",        category: "symboles", fr: "Lune",         en: "Moon",       es: "Luna" },
  { key: "sun",         category: "symboles", fr: "Soleil",       en: "Sun",        es: "Sol" },
  { key: "lightning",   category: "symboles", fr: "Éclair",       en: "Lightning",  es: "Rayo" },
  { key: "crown",       category: "symboles", fr: "Couronne",     en: "Crown",      es: "Corona" },
  { key: "flower",      category: "symboles", fr: "Fleur",        en: "Flower",     es: "Flor" },
];

/**
 * Returns SVG <g>/<path> children rendering an icon, sized in 24×24 viewBox.
 * Color is the fill colour.
 */
export function IconShape({ kind, color = "#ffffff", strokeWidth = 2.4 }) {
  const stroke = { stroke: color, strokeWidth, strokeLinecap: "round", strokeLinejoin: "round" };
  const fill = { fill: color };

  switch (kind) {
    // ---------- Animaux ----------
    case "heart":
      return <path {...stroke} {...fill} d="M12 21s-7-4.6-9-9c-1.6-3.6 1-7.2 4.5-6.8 2 .2 3.2 1.5 4.5 3.2 1.3-1.7 2.5-3 4.5-3.2 3.5-.4 6.1 3.2 4.5 6.8-2 4.4-9 9-9 9z" />;
    case "paw":
      return (
        <g {...stroke} {...fill}>
          <ellipse cx="12" cy="16" rx="4.6" ry="3.6" />
          <circle cx="6" cy="11" r="1.9" />
          <circle cx="18" cy="11" r="1.9" />
          <circle cx="9" cy="6.5" r="1.9" />
          <circle cx="15" cy="6.5" r="1.9" />
        </g>
      );
    case "bone":
      return <path {...stroke} {...fill} d="M5 8.5a2.5 2.5 0 1 1 4-2 2.5 2.5 0 0 1 1.5 2v3a2.5 2.5 0 0 1-1.5 2 2.5 2.5 0 1 1-4-2v-3zm9 0a2.5 2.5 0 1 1 4-2 2.5 2.5 0 0 1 1.5 2v3a2.5 2.5 0 0 1-1.5 2 2.5 2.5 0 1 1-4-2v-3zm-4-1h4v6h-4z" />;
    case "fish":
      return (
        <g {...stroke} {...fill}>
          <path d="M3 12c0-3 4-6 9-6 4 0 7 2 8 5l4-3v12l-4-3c-1 3-4 5-8 5-5 0-9-3-9-7v-3z" />
          <circle cx="15" cy="10" r="1" fill="#0A0A0A" stroke="none" />
        </g>
      );
    case "bird":
      return <path {...stroke} {...fill} d="M22 5c-1 1-2 1-3 1-1-1-2-1-3-1-3 0-5 2-5 5 0 0 0 1 0 1-3-1-6-2-8-5 0 0-1 2 0 4 1 1 2 2 3 2-1 0-2 0-3-1 0 2 2 4 4 5-1 0-2 0-3 0 1 2 4 3 6 3 7 0 11-6 11-11l2-3z" />;
    case "rabbit":
      return (
        <g {...stroke} {...fill}>
          <ellipse cx="9" cy="6" rx="2" ry="4.5" />
          <ellipse cx="15" cy="6" rx="2" ry="4.5" />
          <circle cx="12" cy="15" r="6" />
        </g>
      );
    case "mouse":
      return (
        <g {...stroke} {...fill}>
          <circle cx="12" cy="14" r="6" />
          <circle cx="7" cy="9" r="3" />
          <circle cx="17" cy="9" r="3" />
          <path d="M18 16c2 0 3 1 4 3" stroke={color} strokeWidth={strokeWidth} fill="none" />
        </g>
      );
    case "bowl":
      return (
        <g {...stroke} {...fill}>
          <path d="M3 13h18l-2 7c0 1-1 2-2 2H7c-1 0-2-1-2-2L3 13z" />
          <ellipse cx="8" cy="11" rx="1.4" ry="1" />
          <ellipse cx="12" cy="9" rx="1.4" ry="1" />
          <ellipse cx="16" cy="11" rx="1.4" ry="1" />
        </g>
      );

    // ---------- Symboles ----------
    case "star":
      return <path {...stroke} {...fill} d="M12 3l2.6 5.4 5.9.6-4.4 4 1.3 5.9L12 16.8 6.6 18.9l1.3-5.9-4.4-4 5.9-.6L12 3z" />;
    case "moon":
      return <path {...stroke} {...fill} d="M21 12.8a9 9 0 1 1-9.8-9.8A7 7 0 0 0 21 12.8z" />;
    case "sun":
      return (
        <g {...stroke} {...fill}>
          <circle cx="12" cy="12" r="4" />
          <g stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" fill="none">
            <line x1="12" y1="2" x2="12" y2="4.5" />
            <line x1="12" y1="19.5" x2="12" y2="22" />
            <line x1="2" y1="12" x2="4.5" y2="12" />
            <line x1="19.5" y1="12" x2="22" y2="12" />
            <line x1="4.6" y1="4.6" x2="6.4" y2="6.4" />
            <line x1="17.6" y1="17.6" x2="19.4" y2="19.4" />
            <line x1="4.6" y1="19.4" x2="6.4" y2="17.6" />
            <line x1="17.6" y1="6.4" x2="19.4" y2="4.6" />
          </g>
        </g>
      );
    case "lightning":
      return <path {...stroke} {...fill} d="M13 2L4 14h6l-2 8 11-12h-6l3-8z" />;
    case "crown":
      return <path {...stroke} {...fill} d="M2 19l1-10 5 4 4-7 4 7 5-4 1 10H2zm0 1.5h20v2H2z" />;
    case "flower":
      return (
        <g {...stroke} {...fill}>
          <circle cx="12" cy="5" r="3" />
          <circle cx="5.5" cy="9.5" r="3" />
          <circle cx="18.5" cy="9.5" r="3" />
          <circle cx="8" cy="17" r="3" />
          <circle cx="16" cy="17" r="3" />
          <circle cx="12" cy="11.5" r="2.4" fill="#0A0A0A" stroke="none" />
        </g>
      );

    // ---------- Québec removed in v3.1 ----------

    default:
      return null;
  }
}

/** Standalone preview for picker buttons. */
export function IconPreview({ kind, size = 22, color = "#ffffff" }) {
  if (kind === "none") {
    return (
      <span
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          border: `1.5px dashed ${color}`,
          display: "inline-block",
          opacity: 0.6,
        }}
      />
    );
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <IconShape kind={kind} color={color} />
    </svg>
  );
}
